/**
 * Unified Subgroups section (Manage MVPs setup): roster create/edit + optional
 * existing-subgroup cards (children). MVP assignment UI lives in SubgroupMvpAssignSection.
 * PATCH members supported for edit-after-create.
 */
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import {
  useNpiProfiles, useSubgroups, type NpiProfileRow, type Subgroup,
} from '@/hooks/queries/useMvpQueries';
import { MipsDataService, type MvpDefinition } from '@/lib/mips-data-service';
import { formatClinicianTypeLabel } from '@/lib/mvp/clinician-type';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useToast } from '@/components/ui/use-toast';
import { AlertTriangle, Plus, Users } from 'lucide-react';

function yesNo(v: boolean | null | undefined) {
  if (v === true) return { label: 'Yes', className: 'text-emerald-700 font-medium' };
  if (v === false) return { label: 'No', className: 'text-slate-500' };
  return { label: '—', className: 'text-muted-foreground' };
}

function formatMoney(n: number | null | undefined) {
  if (n == null || Number.isNaN(Number(n))) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD', maximumFractionDigits: 0,
  }).format(Number(n));
}

/** Label + (!) trigger — same pattern as other Configuration help affordances. */
function HintLabel({
  children, hint, className = '',
}: { children: ReactNode; hint: string; className?: string }) {
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            className={`inline-flex items-center gap-1 text-left hover:text-foreground ${className}`}
          >
            <span>{children}</span>
            <span
              className="inline-flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full border border-muted-foreground/40 text-[9px] font-semibold leading-none text-muted-foreground"
              aria-hidden
            >
              !
            </span>
          </button>
        </TooltipTrigger>
        <TooltipContent
          side="top"
          align="start"
          collisionPadding={12}
          className="max-w-sm whitespace-normal break-words text-xs leading-relaxed"
        >
          {hint}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

const HINTS = {
  individual:
    'Count of clinicians on this roster marked MIPS-eligible as an individual in the QPP clinician eligibility file for this practice. Blank in the table means no eligibility row for that NPI.',
  group:
    'Count of clinicians marked MIPS-eligible when reporting with this practice (TIN) — the group column from the same QPP clinician file. This is not practice-level eligibility; it is per clinician under this TIN.',
  colIndividual:
    'Yes = this clinician is MIPS-eligible as an individual (QPP clinician eligibility for this practice). Source: eligibility file, or CMS org match if file data is missing.',
  colGroup:
    'Yes = this clinician is MIPS-eligible when reporting under this practice’s TIN (group). Source: eligibility file, or CMS org match if file data is missing.',
  allowedCharges:
    'Medicare Part B allowed charges from the QPP clinician eligibility file for this practice. Empty when the clinician is claims-only or the file has no charge amount.',
} as const;

interface SubgroupBuilderProps {
  practiceId: string | number;
  programYear: number;
  isSmallPractice: boolean;
  selections: Array<{ id: number; mvp_id: string; entity_type: string; subgroup_id: number | null }>;
  /** Parent (ExploreMvpsTab) already shows reporting / unmatched status */
  hideStatusAlerts?: boolean;
  /** Open manage-members form for this subgroup (from Your subgroups cards) */
  editRequest?: Subgroup | null;
  onEditRequestHandled?: () => void;
  /** Extra header actions (e.g. Export CSV) shown next to New subgroup */
  headerActions?: ReactNode;
  /** Existing subgroup cards — rendered in the same card under create/edit */
  children?: ReactNode;
}

function cleanSpecialty(desc: string | null | undefined) {
  if (!desc) return null;
  return desc.replace(/^Physician\//i, '').trim() || desc;
}

export function SubgroupBuilder({
  practiceId, programYear, isSmallPractice, selections, hideStatusAlerts = false,
  editRequest = null, onEditRequestHandled, headerActions, children,
}: SubgroupBuilderProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const dataService = MipsDataService.getInstance();

  const profilesQ = useNpiProfiles(practiceId, programYear);
  const subgroupsQ = useSubgroups(practiceId, programYear);

  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [newName, setNewName] = useState('');
  const [newSpecialtyHint, setNewSpecialtyHint] = useState<string | null>(null);
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [filterClinician, setFilterClinician] = useState('');
  const [filterSpecialty, setFilterSpecialty] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [filterInd, setFilterInd] = useState<'all' | 'yes' | 'no' | 'unknown'>('all');
  const [filterGrp, setFilterGrp] = useState<'all' | 'yes' | 'no' | 'unknown'>('all');
  const [busy, setBusy] = useState(false);

  const clearRosterFilters = () => {
    setFilterClinician('');
    setFilterSpecialty('all');
    setFilterType('all');
    setFilterInd('all');
    setFilterGrp('all');
  };

  const profiles = profilesQ.data?.profiles ?? [];
  const subgroups = subgroupsQ.data ?? [];
  const multiSpecialty = profilesQ.data?.multiSpecialty ?? false;
  const unmatchedNpis = profilesQ.data?.unmatchedNpis ?? 0;
  const mvps = useMemo(() => dataService.getMvps(programYear), [dataService, programYear]);

  const profileByNpi = useMemo(() => {
    const m = new Map<string, NpiProfileRow>();
    for (const p of profiles) m.set(p.npi, p);
    return m;
  }, [profiles]);

  const suggestedMvpsForCode = useMemo(() => {
    const xwalk = dataService.getSpecialtyCrosswalk();
    return (code: string | null): MvpDefinition[] => {
      if (!code) return [];
      return mvps.filter(m => m.specialtiesMostApplicableTo.some(s => {
        const entry = xwalk.mvpSpecialtyToCodes?.[s];
        const codes = entry?.medicareSpecialtyCodes ?? entry?.codes ?? [];
        return codes.includes(code);
      }));
    };
  }, [dataService, mvps]);

  const assignedNpis = useMemo(() => {
    const set = new Set<string>();
    subgroups.forEach(sg => {
      if (editingId != null && sg.id === editingId) return;
      sg.members.forEach(npi => set.add(npi));
    });
    return set;
  }, [subgroups, editingId]);

  /** NPI → subgroup membership + that subgroup's MVP (for In subgroup column). */
  const membershipByNpi = useMemo(() => {
    const mvpBySg = new Map<number, string>();
    for (const s of selections) {
      if (s.entity_type === 'subgroup' && s.subgroup_id != null) {
        mvpBySg.set(s.subgroup_id, s.mvp_id);
      }
    }
    const m = new Map<string, { subgroupId: number; subgroupName: string; mvpId: string | null }>();
    for (const sg of subgroups) {
      const mvpId = mvpBySg.get(sg.id) ?? null;
      for (const npi of sg.members) {
        m.set(npi, { subgroupId: sg.id, subgroupName: sg.name, mvpId });
      }
    }
    return m;
  }, [subgroups, selections]);

  const specialtyGroups = useMemo(() => {
    const map = new Map<string, { desc: string; npis: string[] }>();
    for (const p of profiles) {
      if (!p.specialty_code || !p.counts_for_practice) continue;
      const g = map.get(p.specialty_code) ?? { desc: p.specialty_desc ?? p.specialty_code, npis: [] };
      g.npis.push(p.npi);
      map.set(p.specialty_code, g);
    }
    return map;
  }, [profiles]);

  const specialtyFilterOptions = useMemo(() => {
    const set = new Set<string>();
    for (const p of profiles) {
      const label = cleanSpecialty(p.specialty_desc) || p.specialty_code;
      if (label) set.add(label);
    }
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [profiles]);

  const typeFilterOptions = useMemo(() => {
    const set = new Set<string>();
    for (const p of profiles) {
      const label = formatClinicianTypeLabel(p.type_description || p.clinician_type);
      if (label) set.add(label);
    }
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [profiles]);

  const matchElig = (v: boolean | null | undefined, filter: 'all' | 'yes' | 'no' | 'unknown') => {
    if (filter === 'all') return true;
    if (filter === 'yes') return v === true;
    if (filter === 'no') return v === false;
    return v == null;
  };

  const filteredProfiles = useMemo(() => {
    const q = filterClinician.trim().toLowerCase();
    return profiles.filter(p => {
      if (q) {
        const hay = [p.clinician_name, p.npi].filter(Boolean).join(' ').toLowerCase();
        if (!hay.includes(q)) return false;
      }
      const specialty = cleanSpecialty(p.specialty_desc) || p.specialty_code || '';
      if (filterSpecialty !== 'all' && specialty !== filterSpecialty) return false;
      const typeLabel = formatClinicianTypeLabel(p.type_description || p.clinician_type) || '';
      if (filterType !== 'all' && typeLabel !== filterType) return false;
      if (!matchElig(p.mips_eligible_individual, filterInd)) return false;
      if (!matchElig(p.mips_eligible_group, filterGrp)) return false;
      return true;
    });
  }, [profiles, filterClinician, filterSpecialty, filterType, filterInd, filterGrp]);

  const filteredAvailable = useMemo(
    () => filteredProfiles.filter(p => !assignedNpis.has(p.npi)),
    [filteredProfiles, assignedNpis],
  );

  const rosterFiltersActive =
    filterClinician.trim() !== ''
    || filterSpecialty !== 'all'
    || filterType !== 'all'
    || filterInd !== 'all'
    || filterGrp !== 'all';

  const selectedProfiles = useMemo(
    () => [...checked].map(npi => profileByNpi.get(npi)).filter(Boolean) as NpiProfileRow[],
    [checked, profileByNpi],
  );

  const selectionSpecialtyBreakdown = useMemo(() => {
    const map = new Map<string, number>();
    for (const p of selectedProfiles) {
      const label = cleanSpecialty(p.specialty_desc) || p.specialty_code || 'Unknown';
      map.set(label, (map.get(label) ?? 0) + 1);
    }
    return [...map.entries()].sort((a, b) => b[1] - a[1]);
  }, [selectedProfiles]);

  const formSuggestedMvps = useMemo(
    () => suggestedMvpsForCode(newSpecialtyHint),
    [suggestedMvpsForCode, newSpecialtyHint],
  );

  const allFilteredAvailableSelected =
    filteredAvailable.length > 0 && filteredAvailable.every(p => checked.has(p.npi));

  const invalidate = () => Promise.all([
    queryClient.invalidateQueries({ queryKey: ['mvp', 'subgroups', practiceId, programYear] }),
    queryClient.invalidateQueries({ queryKey: ['mvp', 'selections', practiceId, programYear] }),
  ]);

  const resetForm = () => {
    setFormOpen(false);
    setEditingId(null);
    setNewName('');
    setChecked(new Set());
    setNewSpecialtyHint(null);
    clearRosterFilters();
  };

  const startCreate = () => {
    setEditingId(null);
    setNewName('');
    setChecked(new Set());
    setNewSpecialtyHint(null);
    clearRosterFilters();
    setFormOpen(true);
  };

  const startEdit = (sg: Subgroup) => {
    setEditingId(sg.id);
    setNewName(sg.name);
    setChecked(new Set(sg.members));
    setNewSpecialtyHint(sg.specialty_hint);
    clearRosterFilters();
    setFormOpen(true);
  };

  useEffect(() => {
    if (!editRequest) return;
    startEdit(editRequest);
    onEditRequestHandled?.();
    requestAnimationFrame(() => {
      document.getElementById('subgroup-roster-form')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- open once per request object
  }, [editRequest]);

  const autoPropose = (code: string) => {
    const g = specialtyGroups.get(code);
    if (!g) return;
    setChecked(new Set(g.npis.filter(npi => !assignedNpis.has(npi))));
    setNewSpecialtyHint(code);
    const specialtyLabel = cleanSpecialty(g.desc) || code;
    setFilterSpecialty(specialtyLabel);
    setFilterClinician('');
    setFilterType('all');
    setFilterInd('all');
    setFilterGrp('all');
    if (!newName.trim()) setNewName(`${specialtyLabel} subgroup`);
  };

  const toggleNpi = (npi: string, on: boolean) => {
    setChecked(prev => {
      const next = new Set(prev);
      if (on) next.add(npi); else next.delete(npi);
      return next;
    });
  };

  /** Select / clear only the currently filtered available rows (keeps other selections). */
  const selectAllFilteredAvailable = (on: boolean) => {
    setChecked(prev => {
      const next = new Set(prev);
      for (const p of filteredAvailable) {
        if (on) next.add(p.npi);
        else next.delete(p.npi);
      }
      return next;
    });
  };

  const saveSubgroup = async () => {
    if (!newName.trim() || checked.size === 0) return;
    setBusy(true);
    try {
      if (editingId != null) {
        await apiClient.patch(`/api/mvp/${practiceId}/subgroups/${editingId}?year=${programYear}`, {
          name: newName.trim(),
          members: [...checked],
          specialty_hint: newSpecialtyHint,
        });
        toast({ title: 'Subgroup updated', description: newName.trim() });
      } else {
        await apiClient.post(`/api/mvp/${practiceId}/subgroups?year=${programYear}`, {
          name: newName.trim(), members: [...checked], specialty_hint: newSpecialtyHint,
        });
        toast({ title: 'Subgroup created', description: `${newName.trim()} (${checked.size} clinicians)` });
      }
      await invalidate();
      resetForm();
    } catch (e: any) {
      toast({
        title: editingId != null ? 'Failed to update subgroup' : 'Failed to create subgroup',
        description: e?.message ?? '',
        variant: 'destructive',
      });
    } finally {
      setBusy(false);
    }
  };

  const specialtyCount = profilesQ.data?.specialtyCodes.length ?? 0;
  const subgroupRequired = multiSpecialty && !isSmallPractice;
  const specialtyHintLabel = newSpecialtyHint
    ? cleanSpecialty(specialtyGroups.get(newSpecialtyHint)?.desc) || newSpecialtyHint
    : null;

  /** Roster MIPS eligibility counts for this practice (TIN), not CMS org rollups. */
  const rosterEligCounts = useMemo(() => {
    let individualYes = 0;
    let groupYes = 0;
    let withData = 0;
    for (const p of profiles) {
      if (p.mips_eligible_individual != null || p.mips_eligible_group != null) withData += 1;
      if (p.mips_eligible_individual === true) individualYes += 1;
      if (p.mips_eligible_group === true) groupYes += 1;
    }
    return { individualYes, groupYes, withData };
  }, [profiles]);

  return (
    <div className="space-y-3">
      {!hideStatusAlerts && subgroupRequired && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            This practice has clinicians in <strong>{specialtyCount} specialties</strong> and is not a small
            practice — CMS requires <strong>subgroup reporting</strong> for MVPs. Build subgroups below and
            assign each its own MVP (group-level commit is de-emphasized in the catalog).
          </AlertDescription>
        </Alert>
      )}
      {!hideStatusAlerts && multiSpecialty && isSmallPractice && (
        <Alert>
          <Users className="h-4 w-4" />
          <AlertDescription>
            Multispecialty roster ({specialtyCount} specialties), but small-practice status exempts this
            practice from the subgroup requirement — group-level MVP reporting stays available.
          </AlertDescription>
        </Alert>
      )}
      {!hideStatusAlerts && unmatchedNpis > 0 && (
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            {unmatchedNpis} clinician{unmatchedNpis === 1 ? '' : 's'} from claims couldn’t be matched to this
            practice by CMS organization name — confirm in Configuration → QPP Reports.
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-start justify-between gap-3">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <Users className="h-4 w-4" />
                Subgroups
              </CardTitle>
              <CardDescription>
                {profiles.length} clinicians on roster
                {profilesQ.data?.lastHydrated
                  ? ` · CMS refreshed ${new Date(profilesQ.data.lastHydrated).toLocaleDateString()}`
                  : ' · CMS not yet refreshed'}
                {subgroups.length > 0
                  ? ` · ${subgroups.length} subgroup${subgroups.length === 1 ? '' : 's'}`
                  : ''}
              </CardDescription>
            </div>
            <div className="flex flex-wrap items-center justify-end gap-2 shrink-0">
              {headerActions}
              {!formOpen && (
                <Button size="sm" variant={subgroupRequired ? 'default' : 'outline'} onClick={startCreate}>
                  <Plus className="h-3.5 w-3.5 mr-1" />New subgroup
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {!formOpen && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div className="rounded-md border bg-slate-50/80 px-3 py-2.5">
                <HintLabel
                  hint={HINTS.individual}
                  className="text-xs font-medium text-muted-foreground"
                >
                  Individual MIPS
                </HintLabel>
                <div className="mt-1 flex items-baseline gap-1.5">
                  <span className="text-xl font-semibold tabular-nums tracking-tight">
                    {rosterEligCounts.individualYes}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    of {profiles.length} on roster
                  </span>
                </div>
              </div>
              <div className="rounded-md border bg-slate-50/80 px-3 py-2.5">
                <HintLabel
                  hint={HINTS.group}
                  className="text-xs font-medium text-muted-foreground"
                >
                  Group MIPS (this TIN)
                </HintLabel>
                <div className="mt-1 flex items-baseline gap-1.5">
                  <span className="text-xl font-semibold tabular-nums tracking-tight">
                    {rosterEligCounts.groupYes}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    of {profiles.length} on roster
                  </span>
                </div>
              </div>
            </div>
          )}
          {rosterEligCounts.withData === 0 && profiles.length > 0 && !formOpen && (
            <p className="text-xs text-muted-foreground">
              No clinician eligibility on file for this practice — import under QPP Reports to fill these counts and table columns.
            </p>
          )}

          {formOpen && (
            <div id="subgroup-roster-form" className="rounded-md border p-3 space-y-3 bg-slate-50/40">
              <div className="text-sm font-medium">
                {editingId != null ? 'Manage members' : 'New subgroup'}
              </div>

              <div className="flex flex-wrap items-end gap-2">
                <Label className="grid gap-1 text-xs font-medium text-muted-foreground">
                  Subgroup name
                  <Input
                    className="w-72 h-9 bg-white"
                    placeholder="e.g. Cardiology subgroup"
                    value={newName}
                    onChange={e => setNewName(e.target.value)}
                  />
                </Label>
                {specialtyGroups.size > 0 && (
                  <Label className="grid gap-1 text-xs font-medium text-muted-foreground">
                    Auto-propose roster
                    <Select onValueChange={autoPropose}>
                      <SelectTrigger className="w-72 h-9 text-xs bg-white">
                        <SelectValue placeholder="Pick a specialty…" />
                      </SelectTrigger>
                      <SelectContent>
                        {[...specialtyGroups.entries()].map(([code, g]) => (
                          <SelectItem key={code} value={code}>
                            {cleanSpecialty(g.desc)} — {g.npis.length} clinician{g.npis.length === 1 ? '' : 's'}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Label>
                )}
              </div>

              {/* Compact selection status — details live in the table below */}
              <div className="rounded-md border bg-white px-3 py-2.5 space-y-1.5">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="text-sm font-medium">
                    {checked.size === 0
                      ? 'No clinicians selected'
                      : `${checked.size} selected`}
                  </p>
                  {checked.size > 0 && checked.size < 2 && (
                    <span className="text-xs font-medium text-amber-800">Needs 2+ for CMS</span>
                  )}
                  {checked.size >= 2 && (
                    <span className="text-xs font-medium text-emerald-700">CMS minimum met</span>
                  )}
                </div>
                {specialtyHintLabel && (
                  <p className="text-sm text-muted-foreground">
                    Specialty · <span className="text-foreground">{specialtyHintLabel}</span>
                  </p>
                )}
                {selectionSpecialtyBreakdown.length > 1 && (
                  <p className="text-xs text-amber-800">
                    Mixed specialties in selection:{' '}
                    {selectionSpecialtyBreakdown.map(([label, n]) => `${label} (${n})`).join(', ')}
                  </p>
                )}
                {formSuggestedMvps[0] && (
                  <p className="text-sm text-muted-foreground">
                    Suggested MVP ·{' '}
                    <span className="text-foreground">
                      {formSuggestedMvps[0].mvpId} {formSuggestedMvps[0].title}
                    </span>
                    {formSuggestedMvps.length > 1 && (
                      <span className="text-muted-foreground">
                        {' '}(+{formSuggestedMvps.length - 1} more — assign after create)
                      </span>
                    )}
                  </p>
                )}
              </div>

              <div className="space-y-3">
                <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-5">
                  <Label className="grid gap-1.5 text-xs font-medium text-muted-foreground min-w-0">
                    Clinician
                    <Input
                      className="bg-white"
                      placeholder="Name or NPI"
                      value={filterClinician}
                      onChange={e => setFilterClinician(e.target.value)}
                    />
                  </Label>
                  <Label className="grid gap-1.5 text-xs font-medium text-muted-foreground min-w-0">
                    Clinician type
                    <Select value={filterType} onValueChange={setFilterType}>
                      <SelectTrigger className="bg-white">
                        <SelectValue placeholder="Any type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Any type</SelectItem>
                        {typeFilterOptions.map(t => (
                          <SelectItem key={t} value={t}>{t}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Label>
                  <Label className="grid gap-1.5 text-xs font-medium text-muted-foreground min-w-0">
                    Specialty
                    <Select value={filterSpecialty} onValueChange={setFilterSpecialty}>
                      <SelectTrigger className="bg-white">
                        <SelectValue placeholder="Any specialty" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Any specialty</SelectItem>
                        {specialtyFilterOptions.map(s => (
                          <SelectItem key={s} value={s}>{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Label>
                  <Label className="grid gap-1.5 text-xs font-medium text-muted-foreground min-w-0">
                    Individual MIPS
                    <Select value={filterInd} onValueChange={v => setFilterInd(v as typeof filterInd)}>
                      <SelectTrigger className="bg-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Any</SelectItem>
                        <SelectItem value="yes">Yes</SelectItem>
                        <SelectItem value="no">No</SelectItem>
                        <SelectItem value="unknown">Blank</SelectItem>
                      </SelectContent>
                    </Select>
                  </Label>
                  <Label className="grid gap-1.5 text-xs font-medium text-muted-foreground min-w-0">
                    Group MIPS
                    <Select value={filterGrp} onValueChange={v => setFilterGrp(v as typeof filterGrp)}>
                      <SelectTrigger className="bg-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Any</SelectItem>
                        <SelectItem value="yes">Yes</SelectItem>
                        <SelectItem value="no">No</SelectItem>
                        <SelectItem value="unknown">Blank</SelectItem>
                      </SelectContent>
                    </Select>
                  </Label>
                </div>
                <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 text-sm text-muted-foreground">
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                    <span>
                      Showing {filteredProfiles.length} of {profiles.length}
                      {rosterFiltersActive ? ' (filtered)' : ''}
                    </span>
                    {rosterFiltersActive && (
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        className="h-8 text-muted-foreground"
                        onClick={clearRosterFilters}
                      >
                        Clear filters
                      </Button>
                    )}
                  </div>
                  <Label className="flex items-center gap-2 font-normal cursor-pointer text-foreground">
                    <Checkbox
                      checked={allFilteredAvailableSelected}
                      onCheckedChange={v => selectAllFilteredAvailable(v === true)}
                      disabled={busy || filteredAvailable.length === 0}
                    />
                    Select all shown ({filteredAvailable.length})
                  </Label>
                </div>
              </div>

              <div className="max-h-80 overflow-auto rounded-md border bg-white">
                <Table>
                  <TableHeader className="sticky top-0 z-10 bg-white shadow-sm">
                    <TableRow className="hover:bg-white">
                      <TableHead className="w-10 pl-3" />
                      <TableHead className="min-w-[9rem]">Clinician</TableHead>
                      <TableHead className="w-[6.5rem]">NPI</TableHead>
                      <TableHead className="min-w-[5.5rem]">Clinician type</TableHead>
                      <TableHead className="min-w-[7rem]">Specialty</TableHead>
                      <TableHead className="whitespace-nowrap">
                        <HintLabel hint={HINTS.colIndividual} className="text-xs font-medium text-muted-foreground">
                          Individual MIPS
                        </HintLabel>
                      </TableHead>
                      <TableHead className="whitespace-nowrap">
                        <HintLabel hint={HINTS.colGroup} className="text-xs font-medium text-muted-foreground">
                          Group MIPS
                        </HintLabel>
                      </TableHead>
                      <TableHead className="whitespace-nowrap text-right">
                        <span className="inline-flex justify-end w-full">
                          <HintLabel hint={HINTS.allowedCharges} className="text-xs font-medium text-muted-foreground">
                            Allowed charges
                          </HintLabel>
                        </span>
                      </TableHead>
                      <TableHead className="min-w-[8.5rem]">In subgroup</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredProfiles.map(p => {
                      const membership = membershipByNpi.get(p.npi);
                      const taken = !!membership && membership.subgroupId !== editingId;
                      const selected = checked.has(p.npi);
                      const typeRaw = p.type_description || p.clinician_type;
                      const typeLabel = formatClinicianTypeLabel(typeRaw);
                      const specialty = cleanSpecialty(p.specialty_desc);
                      const ind = yesNo(p.mips_eligible_individual);
                      const grp = yesNo(p.mips_eligible_group);
                      const inThisEdit = editingId != null && membership?.subgroupId === editingId;
                      return (
                        <TableRow
                          key={p.npi}
                          className={`${taken ? 'opacity-50' : 'cursor-pointer'} ${
                            selected && !taken ? 'bg-primary/5 hover:bg-primary/10' : ''
                          }`}
                          onClick={() => {
                            if (!taken && !busy) toggleNpi(p.npi, !selected);
                          }}
                        >
                          <TableCell className="pl-3" onClick={e => e.stopPropagation()}>
                            <Checkbox
                              checked={selected}
                              disabled={taken || busy}
                              onCheckedChange={v => toggleNpi(p.npi, v === true)}
                            />
                          </TableCell>
                          <TableCell>
                            <div className="font-medium leading-tight">
                              {p.clinician_name || (
                                <span className="text-muted-foreground font-normal italic">Name unavailable</span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="font-mono text-xs text-muted-foreground tabular-nums">
                            {p.npi}
                          </TableCell>
                          <TableCell className="text-xs">
                            <span title={typeRaw ?? undefined}>
                              {typeLabel || '—'}
                            </span>
                          </TableCell>
                          <TableCell className="text-xs">
                            {specialty || '—'}
                          </TableCell>
                          <TableCell className={`text-center text-xs ${ind.className}`}>{ind.label}</TableCell>
                          <TableCell className={`text-center text-xs ${grp.className}`}>{grp.label}</TableCell>
                          <TableCell className="text-right text-xs tabular-nums text-muted-foreground">
                            {formatMoney(p.allowed_charges)}
                          </TableCell>
                          <TableCell className="text-xs">
                            {membership && !inThisEdit ? (
                              membership.mvpId ? (
                                <span
                                  className="font-mono font-medium text-foreground"
                                  title={membership.subgroupName}
                                >
                                  {membership.mvpId}
                                </span>
                              ) : (
                                <span className="text-amber-800" title={membership.subgroupName}>
                                  Awaiting MVP selection
                                </span>
                              )
                            ) : selected || inThisEdit ? (
                              <span className="text-muted-foreground">This subgroup</span>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {filteredProfiles.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={9} className="text-sm text-muted-foreground text-center py-8">
                          {profiles.length === 0
                            ? 'No roster yet — import QPP eligibility or upload claims (QPP Reports).'
                            : 'No clinicians match these filters.'}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <Button
                  size="sm"
                  disabled={busy || !newName.trim() || checked.size === 0}
                  onClick={saveSubgroup}
                >
                  {editingId != null ? `Save (${checked.size})` : `Create (${checked.size})`}
                </Button>
                <Button size="sm" variant="outline" onClick={resetForm}>Cancel</Button>
              </div>
            </div>
          )}

          {subgroups.length === 0 && !formOpen && (
            <div className="text-sm text-muted-foreground">
              {subgroupRequired
                ? 'No subgroups yet — create one per specialty path and assign each an MVP.'
                : 'No subgroups yet. Only needed when CMS requires subgroup reporting for a multispecialty practice.'}
            </div>
          )}

          {children && (
            <div className={formOpen || subgroups.length === 0 ? '' : 'border-t border-slate-200/80 pt-4'}>
              {children}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
